import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import type { NewMaterialV1 } from '@ilteducation/content-registry-types';
import type { Language } from '@ilteducation/content-ref-data-types';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';
import { defaultRealms } from '@/foundations/helpers/defaultRealms';
import { createBulk } from '@/data-provider/materials';
import { getAllRefData } from '@/data-provider/refDataProvider';
import DropFile from '@/components/base/DropFile.vue';
import eventBus from '@/foundations/services/eventBus';
import useMaterialUtil from './use-material-util';
import parseMaterialFileName from './parse-material-file-name';
import useMaterial from './use-material';
import useUploadMedia from '@/comp/use-upload-media';
import MarketSelect from '@/components/material/MarketSelect.vue';
import { useRouter } from 'vue-router/composables';
import { NewMaterialV1WithTmpFile } from '@/resources/materials/types';

type MaterialWithFile = NewMaterialV1 & {
  tmpFile?: File;
  fileName?: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'BulkCreateForm',
  setup(__props) {

const router = useRouter();
const { newMaterial, languageRealm } = useMaterialUtil();
const { uploadMedia, isUploadInProgress } = useMaterial();
const { upload } = useUploadMedia();

// Form related
const form = ref();
const isFormValid = ref(false);
const saveInProgress = ref(false);
const uploadedFiles = ref<File[]>([]);

// Global settings (applied to all materials)
const globalRealm = ref<string[]>([]);
const globalLanguage = ref<string | undefined>(undefined);
const globalSubscriptionType = ref<string[]>([]);

// Materials list
const materials = ref<MaterialWithFile[]>([]);

const availableLanguages = ref([] as Language[]);
getAllRefData('languages').then((languages) => {
  availableLanguages.value = languages.sort((a: Language, b: Language) => a.language.localeCompare(b.language));
});

const availableSubscriptions = defaultSubscriptions;
const availableMarkets = defaultRealms;

const rules = {
  required: (value: unknown) => !!value || 'This field is required',
  requiredMultiple: (value: unknown[]) => value.length > 0 || 'This field is required',
  notEmptyRule: (val: any) => (val || '').length > 0 || 'This field is required',
  trimmedString: (val: string) =>
    !/^\s+|\s+$|\s{2,}/.test(val) || 'Leading, trailing, or multiple whitespaces in a row are not allowed',
};

const marketSelectRules = {
  notEmptyRule: (v: string[]) => v.length > 0,
};

const headers = computed(() => [
  { text: '#', value: 'index'},
  { text: 'File Name', value: 'fileName' },
  { text: 'Title', value: 'title'},
  { text: 'Language', value: 'language'},
  { text: 'Actions', value: 'actions'},
]);

const isInProgress = computed(() => saveInProgress.value || isUploadInProgress.value);

const onFileUpload = (files: File[]) => {
  uploadedFiles.value.push(...files);

  files.forEach((file) => {
    // Try to parse filename: Title_Language.pdf
    const parsed = parseMaterialFileName(file.name);

    const title = parsed ? parsed.title : file.name.replace(/\.pdf$/i, '');
    const language = globalLanguage.value;

    const material: MaterialWithFile = {
      ...newMaterial({
        title,
        language,
        realm: language && languageRealm[language] ? languageRealm[language] : undefined,
        resourceType: 'pdf',
      }),
      tmpFile: file,
      fileName: file.name,
    };

    materials.value.push(material);
  });
};

const removeMaterial = (index: number) => {
  materials.value.splice(index, 1);
  uploadedFiles.value.splice(index, 1);
};

const onCombinedRealm = (newSelection: string[]) => {
  const filteredNewSelection = newSelection.filter((realm) =>
    availableMarkets.some((market) => market.code === realm)
  );
  globalRealm.value = filteredNewSelection;
};

const onSave = async () => {
  if (!form.value.validate()) {
    eventBus.$emit('snackbarV2.open', { text: 'Please fill in all required fields', color: 'error' });
    return;
  }

  if (materials.value.length === 0) {
    eventBus.$emit('snackbarV2.open', { text: 'Please upload at least one PDF', color: 'error' });
    return;
  }

  saveInProgress.value = true;

  try {
    // Apply global settings to all materials
    const materialsToCreate: NewMaterialV1WithTmpFile[] = materials.value.map((material) => {
      const realm = globalRealm.value.length > 0 ? globalRealm.value[0] : (material.realm || '');
      const availabilityRealms = globalRealm.value.length > 0 ? globalRealm.value : [realm];
      return {
        type: 'MaterialV1',
        language: material.language!,
        realm,
        resourceType: 'pdf' as const,
        title: material.title,
        originalTitle: material.title,
        properties: {
          ...material.properties,
        },
        availabilityRealms: availabilityRealms as [string, ...string[]],
        accessRules: {
          subscriptionType: globalSubscriptionType.value,
        },
        ages: {},
        tmpFile: material!.tmpFile,
      };
    });

    //pageNr=1

    // Create all materials
    const createdMaterials = await createBulk(materialsToCreate);

    // Upload files for each created material
    await Promise.all(
      createdMaterials.map(async (material, index) => {
        const file = materials.value[index].tmpFile;
        if (file && material.id) {
          await uploadMedia(material.id, file, { pageNr: 1 });
        }
      })
    );

    eventBus.$emit('snackbarV2.open', {
      text: `Successfully created ${createdMaterials.length} material(s)`,
      color: 'success'
    });

    // Navigate to materials list
    await router.push('/materials');
  } catch (error) {
    console.error('Error during bulk creation:', error);
    const errorMessage = error instanceof Error ? error.message : 'Error creating materials';
    eventBus.$emit('snackbarV2.open', { text: errorMessage, color: 'error' });
  } finally {
    saveInProgress.value = false;
  }
};

const clearAll = () => {
  materials.value = [];
  uploadedFiles.value = [];
};

return { __sfc: true,router, newMaterial, languageRealm, uploadMedia, isUploadInProgress, upload, form, isFormValid, saveInProgress, uploadedFiles, globalRealm, globalLanguage, globalSubscriptionType, materials, availableLanguages, availableSubscriptions, availableMarkets, rules, marketSelectRules, headers, isInProgress, onFileUpload, removeMaterial, onCombinedRealm, onSave, clearAll, DropFile, MarketSelect }
}

})